import { create } from 'zustand'
import { persist } from 'zustand/middleware'
import { api } from '@/lib/api'

interface User {
  userId: number
  userName: string
  email: string | null
  customerName: string
  customerId: number
  isAdmin: boolean
  permissions: string[]
}

interface RateLimitStatus {
  blocked: boolean
  remainingAttempts: number
  blockedUntil: string | null
}

interface AuthState {
  user: User | null
  token: string | null
  isAuthenticated: boolean
  isLoading: boolean
  error: string | null
  rateLimitStatus: RateLimitStatus | null
  
  // Actions
  login: (userName: string, password: string) => Promise<boolean>
  logout: () => Promise<void>
  checkAuth: () => Promise<void>
  clearError: () => void
  checkRateLimit: (userName: string) => Promise<RateLimitStatus>
}

export const useAuthStore = create<AuthState>()(
  persist(
    (set, get) => ({
      user: null,
      token: null,
      isAuthenticated: false,
      isLoading: false,
      error: null,
      rateLimitStatus: null,

      login: async (userName: string, password: string) => {
        set({ isLoading: true, error: null })
        
        try {
          const response = await api.post('/bi/auth/login', {
            user_name: userName,
            password,
          })

          if (response.data.success) {
            const { token, user } = response.data.data
            
            // Set token in API client
            api.defaults.headers.common['Authorization'] = `Bearer ${token}`
            
            set({
              user,
              token,
              isAuthenticated: true,
              isLoading: false,
              error: null,
              rateLimitStatus: null,
            })
            
            return true
          } else {
            // Handle rate limit or auth error
            const errorData = response.data
            
            if (errorData.code === 'RATE_LIMITED') {
              set({
                isLoading: false,
                error: 'Demasiados intentos. Intente nuevamente más tarde.',
                rateLimitStatus: {
                  blocked: true,
                  remainingAttempts: 0,
                  blockedUntil: errorData.blockedUntil,
                },
              })
            } else {
              set({
                isLoading: false,
                error: errorData.error || 'Credenciales inválidas',
                rateLimitStatus: errorData.remainingAttempts !== undefined
                  ? {
                      blocked: false,
                      remainingAttempts: errorData.remainingAttempts,
                      blockedUntil: null,
                    }
                  : null,
              })
            }
            
            return false
          }
        } catch (error: any) {
          const errorResponse = error.response?.data
          
          if (errorResponse?.code === 'RATE_LIMITED') {
            set({
              isLoading: false,
              error: 'Demasiados intentos. Intente nuevamente más tarde.',
              rateLimitStatus: {
                blocked: true,
                remainingAttempts: 0,
                blockedUntil: errorResponse.blockedUntil,
              },
            })
          } else {
            set({
              isLoading: false,
              error: errorResponse?.error || 'Error de conexión',
              rateLimitStatus: errorResponse?.remainingAttempts !== undefined
                ? {
                    blocked: false,
                    remainingAttempts: errorResponse.remainingAttempts,
                    blockedUntil: null,
                  }
                : null,
            })
          }
          
          return false
        }
      },

      logout: async () => {
        try {
          await api.post('/bi/auth/logout')
        } catch (error) {
          // Ignore logout errors
        } finally {
          delete api.defaults.headers.common['Authorization']
          set({
            user: null,
            token: null,
            isAuthenticated: false,
            error: null,
            rateLimitStatus: null,
          })
        }
      },

      checkAuth: async () => {
        const { token } = get()
        
        if (!token) {
          set({ isAuthenticated: false })
          return
        }

        try {
          api.defaults.headers.common['Authorization'] = `Bearer ${token}`
          const response = await api.get('/bi/auth/me')
          
          if (response.data.success) {
            set({
              user: response.data.data.user,
              isAuthenticated: true,
            })
          } else {
            set({
              user: null,
              token: null,
              isAuthenticated: false,
            })
          }
        } catch (error) {
          delete api.defaults.headers.common['Authorization']
          set({
            user: null,
            token: null,
            isAuthenticated: false,
          })
        }
      },

      clearError: () => set({ error: null }),

      checkRateLimit: async (userName: string) => {
        try {
          const response = await api.get(`/bi/auth/rate-limit-status/${userName}`)
          
          if (response.data.success) {
            const status = response.data.data
            set({ rateLimitStatus: status })
            return status
          }
          
          return {
            blocked: false,
            remainingAttempts: 10,
            blockedUntil: null,
          }
        } catch (error) {
          return {
            blocked: false,
            remainingAttempts: 10,
            blockedUntil: null,
          }
        }
      },
    }),
    {
      name: 'xafra-bi-auth',
      partialize: (state) => ({
        token: state.token,
        user: state.user,
        isAuthenticated: state.isAuthenticated,
      }),
    }
  )
)
